import BaseDropdownController from './base_dropdown_controller'

export default class extends BaseDropdownController {
  static targets = ["checkbox", "countDisplay", "applyBtn", "dropdownToggle", "searchInput", "item", "emptyState"]
  static values = { filterType: String }

  connect() {
    super.connect()
    this.updateCountDisplay()
    this.filterVisibleItems()
    this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
    
    // Listen for turbo frame loads to update display after form submissions
    document.addEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
  }

  disconnect() {
    super.disconnect()
    document.removeEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
  }

  handleTurboFrameLoad(event) {
    // Update display when the auction results frame loads (after form submission)
    if (event.target.id === 'auction_results') {
      setTimeout(() => {
        this.updateCountDisplay()
      }, 50) // Small delay to ensure form is fully updated
    }
  }

  toggleItem(event) {
    this.updateCountDisplay()
  }

  filterItems() {
    this.filterVisibleItems()
  }

  toggleItemByClick(event) {
    if (event.target.type === 'checkbox') {
      return
    }
    
    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.checked = !checkbox.checked
      this.updateCountDisplay()
    }
  }

  applyFilters() {
    const shouldRefreshDependents = this.shouldRefreshDependents()

    this.syncWithFormCheckboxes()
    if (shouldRefreshDependents) {
      this.clearDependentFormState()
      this.updateDependentDropdowns()
    }
    this.closeDropdown()
    this.closeOffcanvas()
    this.submitSharedForm()
  }

  syncWithFormCheckboxes() {
    this.checkboxTargets.forEach(checkbox => {
      const formCheckbox = document.querySelector(this.formCheckboxSelector(checkbox.value))
      if (formCheckbox) {
        formCheckbox.checked = checkbox.checked
      }
    })
  }

  clearAll() {
    const shouldRefreshDependents = this.isMunicipalityFilter()

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })

    this.syncWithFormCheckboxes()
    this.updateCountDisplay()
    if (shouldRefreshDependents) {
      this.clearDependentFormState()
      this.updateDependentDropdowns()
    }
    this.submitSharedForm()
  }

  updateCountDisplay() {
    if (!this.hasCountDisplayTarget) return

    const appliedCount = this.countAppliedFilters()
    const currentText = this.countDisplayTarget.textContent
    const baseText = currentText.replace(/\(\d+\)/, '')

    if (appliedCount === 0) {
      this.countDisplayTarget.textContent = baseText.trim()
    } else {
      this.countDisplayTarget.textContent = `${baseText.trim()} (${appliedCount})`
    }

    if (this.hasDropdownToggleTarget) {
      if (appliedCount === 0) {
        this.dropdownToggleTarget.classList.remove('filter-dropdown-active')
      } else {
        this.dropdownToggleTarget.classList.add('filter-dropdown-active')
      }
    }
  }

  countAppliedFilters() {
    let count = 0
    const form = document.getElementById('auction-filters-form')
    if (!form) return count

    const appliedCheckboxes = form.querySelectorAll(`[data-${this.filterType()}-checkbox]:checked`)
    count += appliedCheckboxes.length

    return count
  }

  updateDependentDropdowns() {
    if (!this.isMunicipalityFilter()) return

    const url = new URL('/auctions/neighborhoods', window.location.origin)

    this.selectedMarketIds().forEach(marketId => {
      url.searchParams.append('selected_markets[]', marketId)
    })

    this.selectedValues().forEach(municipality => {
      url.searchParams.append('selected_municipalities[]', municipality)
    })

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error updating neighborhoods dropdown:', error)
    })
  }

  clearDependentFormState() {
    if (!this.isMunicipalityFilter()) return

    document.querySelectorAll('[data-neighborhood-checkbox]').forEach(checkbox => {
      checkbox.checked = false
    })
  }

  selectedMarketIds() {
    return Array.from(document.querySelectorAll('[data-market-checkbox]:checked')).map(checkbox => checkbox.value)
  }

  selectedValues() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked).map(checkbox => checkbox.value)
  }

  appliedValues() {
    return Array.from(document.querySelectorAll(`[data-${this.filterType()}-checkbox]:checked`)).map(checkbox => checkbox.value)
  }

  formCheckboxSelector(value) {
    return `[data-${this.filterType()}-checkbox="${this.escapeSelectorValue(value)}"]`
  }

  filterType() {
    return this.filterTypeValue || 'municipality'
  }

  isMunicipalityFilter() {
    return this.filterType() === 'municipality'
  }

  shouldRefreshDependents() {
    if (!this.isMunicipalityFilter()) return false

    return this.selectedValues().slice().sort().join('|') !== this.appliedValues().slice().sort().join('|')
  }

  escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value)
    }

    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
  }

  filterVisibleItems() {
    if (!this.hasSearchInputTarget || !this.hasItemTarget) return

    const query = this.normalizedText(this.searchInputTarget.value)
    let visibleCount = 0

    this.itemTargets.forEach(item => {
      const matches = this.normalizedText(item.dataset.filterLabel).includes(query)
      item.classList.toggle('d-none', !matches)
      if (matches) visibleCount += 1
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('d-none', visibleCount > 0)
    }
  }

  normalizedText(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
  }
}
